// libraries
import React, { useContext, useEffect, useState, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { startCase, toLower } from 'lodash'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Barcode from 'react-barcode'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTheme } from '@mui/material/styles'
import { Box, Typography, Stack, InputAdornment, IconButton, Tooltip } from '@mui/material'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PrintIcon from '@mui/icons-material/Print'
import EmailIcon from '@mui/icons-material/Email'
import UndoIcon from '@mui/icons-material/Undo'
import LockIcon from '@mui/icons-material/Lock'
import printJS from 'print-js'

// state
import { clearPaymentInfo, setPaymentInfo } from 'state/payment'
import { clearTransportationInformation, setPurchaseOrderInfo } from 'state/inboundTicket'
import { clearMergeTicketsState } from 'state/bill'

// queries
import {
  useGetBillQuery,
  useUpdateBillMutation,
  useMergeBillMutation,
  useUpdateAndMergeBillMutation,
  useVoidBillMutation,
} from 'services/bills'

import { useGetCustomerQuery } from 'services/customers'
import { useCreatePaymentMutation, useUpdatePaymentMutation } from 'services/payments'
import { useGetInboundTicketsByIdsMutation } from 'services/inboundTickets'
import { useGetOutboundTicketsByIdsMutation } from 'services/outboundTickets'
import { useGetOrgImageQuery } from 'services/assets'

// specific components
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import BillTicketsTable from 'components/containers/bills/components/BillTicketsTable'
import BillFooter from 'components/containers/bills/components/BillFooter'
import BillPaymentsModal from 'components/containers/bills/components/BillPaymentsModal'
import BillPaymentsTable from 'components/containers/bills/components/BillPaymentsTable'
import PrintCheckBlade from 'components/containers/bills/components/PrintCheckBlade'

// generic components
import { Chip } from 'components/generic/chip'
import InvoicedStatusChip from 'components/generic/status/InvoicedStatusChip'
import AlertSnackbar from 'components/generic/snackbars'
import Select from 'components/generic/form/Select'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import DatePicker from 'components/generic/form/DatePicker'
import PrintDialog from 'components/generic/printable/PrintDialog'
import PrintableBill from 'components/generic/printable/PrintableBill'
import CheckTemplate from 'components/generic/printable/CheckTemplate'
import MaterialTypesDataFetcher from 'components/generic/MaterialTypesDataFetcher'
import EmailModal from 'components/generic/modals/dialog-modal/EmailModal'
import SkeletonWrapper from 'components/generic/SkeletonWrapper'

// helpers and utils
import {
  BILL_STATUSES,
  BILL_ACTION,
  CURRENCY_OPTIONS,
  PAYMENT_METHODS_VALUES,
  PRINT_DIALOG_TYPES,
  BILL_TYPES,
  CUSTOMERS_FILTERS,
  ROUNDING_PRECISIONS,
  ENTITY_IN_LOCK_PERIOD_MESSAGE,
  TICKET_STATUSES,
  PDF_TYPE,
} from 'utils/constants'
import { formOptions } from 'utils/validation/bill'
import {
  dispatchSnackbar,
  setDecimalPrecision,
  isPrintableCheckPayment,
  locationIsNotEmpty,
  checkIfDateIsLocked,
  generateLockTooltip,
} from 'utils/helpers'
import { getContactAddress, addToDate } from 'utils/data-manipulation/common'
import CurrencyToWords from 'utils/data-manipulation/currencyToWords'
import { dateFormatter, roundTo } from 'utils/data-manipulation/valueFormatter'
import { request } from 'customRequest'
import { ACTION_TYPE } from 'components/generic/buttons/ButtonList'
import useGetPricePrepend from 'utils/custom-hooks/useGetPricePrepend'
import { materialsHasTareRequiredAndMissing } from 'components/containers/bills/utils'
import usePaymentAuthorization from 'components/containers/bills/components/usePaymentAuthorization'
import HighBalancePaymentAlert from 'components/containers/bills/components/HighBalancePaymentAlert'
import useQzTray from 'utils/custom-hooks/useQzTray'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'

const BillContainer = ({ defaultBillType }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const navigate = useNavigate()

  const { id: billId } = useParams()
  const isUpdate = !!billId

  const { handleSubmit, control, formState, reset, getValues, setValue, watch } = useForm(formOptions)
  const formRef = useRef()
  const { errors, dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length

  const contentContext = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)

  const [hasEditedInboundTickets, setHasEditedInboundTickets] = useState(false)
  const [selectedTicketsData, setSelectedTicketsData] = useState([])
  const [loadingPaymentsData, setLoadingPaymentsData] = useState(false)
  const [barcode, setBarcode] = useState(null)
  const [selectedCustomerId, setSelectedCustomerId] = useState(null)
  const [billIsFullyPaid, setBillIsFullyPaid] = useState(false)
  const [paymentMade, setPaymentMade] = useState(false)
  const [totalGross, setTotalGross] = useState(0)
  const [totalTare, setTotalTare] = useState(0)
  const [totalAdjustments, setTotalAdjustments] = useState(0)
  const [totalNet, setTotalNet] = useState(0)
  const [subtotalValue, setSubtotalValue] = useState(0)
  const [taxValue, setTaxValue] = useState(0)
  const [totalValue, setTotalValue] = useState(0)
  const [remainingBalance, setRemainingBalance] = useState(0)
  const [paymentsData, setPaymentsData] = useState([])
  const [openBillPaymentsModal, setOpenBillPaymentsModal] = useState(false)
  const [openPrintCheckBlade, setOpenPrintCheckBlade] = useState(false)
  const [openPrintCheckPreview, setOpenPrintCheckPreview] = useState(false)
  const [openPrintBillDialog, setOpenPrintBillDialog] = useState(false)
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isFetchingPdf, setIsFetchingPdf] = useState(false)
  const { mergeTickets: mergeTicketsState } = useSelector(state => state.bill)
  const [cachedMaterialTypesData, setCachedMaterialTypesData] = useState({})
  const [hasLockedTickets, setHasLockedTickets] = useState(false)
  const { enablePdfEmails, enableFePdfs, enableLockAccountingV2, enableNegativeIbtToInvoice } = useSelector(
    state => state.featureFlags?.variables || {},
  )

  const checkPaymentsData = useMemo(
    () => paymentsData?.filter(payment => payment?.paymentMethod === PAYMENT_METHODS_VALUES.CHECK.toLowerCase()) || [],
    [paymentsData],
  )
  const unprintedCheckPaymentsData = useMemo(() => paymentsData?.filter(isPrintableCheckPayment) || [], [paymentsData])

  const validationSchema = {}
  unprintedCheckPaymentsData.forEach(payment => {
    validationSchema[payment.id] = Yup.object().shape({
      details: Yup.object().shape({
        payFrom: Yup.string().required('Pay From is Required'),
        checkNumber: Yup.string().required('Check Number is Required'),
      }),
    })
  })

  const {
    handleSubmit: checkPrintHandleSubmit,
    control: checkPrintControl,
    reset: checkPrintReset,
    getValues: checkPrintGetValues,
    setValue: checkPrintSetValue,
    watch: checkPrintWatch,
    trigger: checkPrintTrigger,
    formState: { errors: checkPrintErrors, isValid: checkPrintIsValid },
  } = useForm({ resolver: yupResolver(Yup.object().shape(validationSchema)) })

  const defaultPaymentMethod = PAYMENT_METHODS_VALUES.CHECK.toLowerCase()
  const { defaultCurrency, settings } = useSelector(state => state.settings)
  const { checkSignatureImage } = settings?.purchaseSettings?.payment || {}
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}
  const { qzPrint } = useQzTray()

  const { data: billData, error: billError, isLoading: isLoadingBill } = useGetBillQuery({ billId }, { skip: !billId })

  const { data: customerData, isLoading: isLoadingCustomerData } = useGetCustomerQuery(
    { customerId: selectedCustomerId },
    { refetchOnMountOrArgChange: true, skip: !selectedCustomerId },
  )

  const { data: checkSignatureImageSrc, isLoading: isLoadingCheckSignatureImage } = useGetOrgImageQuery(
    { filename: checkSignatureImage, isTemp: false },
    { skip: !checkSignatureImage },
  )

  const { billIsVoid, billIsInvoiced, billType, billTypeIsMaterial } = useMemo(() => {
    const type = billData?.type || defaultBillType
    return {
      billIsVoid: billData?.status?.toUpperCase() === BILL_STATUSES?.VOID?.id,
      billIsInvoiced: billData?.status?.toUpperCase() === BILL_STATUSES?.INVOICED?.id,
      billType: type,
      billTypeIsMaterial: type === BILL_TYPES.MATERIAL.id,
    }
  }, [billData, defaultBillType])

  const billTaxRate = useMemo(
    () =>
      parseFloat(
        !billData || billData?.status?.toUpperCase() === BILL_STATUSES.DUE.id
          ? customerData?.taxRate
          : billData?.taxRate,
      ) || 0,
    [billData, customerData],
  )

  // redirect back to list view if bill is not found (likely attempting to view a bill for a different yard)
  useEffect(() => {
    if (billError && billError.status === 404) {
      navigate(defaultBillType === BILL_TYPES.MATERIAL.id ? '/app/purchases/bills' : '/app/freight/freight-bills')
    }
  }, [billError, navigate, defaultBillType])

  const [updateBill, { isLoading: isLoadingUpdateBill }] = useUpdateBillMutation()

  const [mergeBill, { isLoading: isLoadingMergeBill }] = useMergeBillMutation()

  const [updateAndMergeBill, { isLoading: isLoadingUpdateAndMergeBill }] = useUpdateAndMergeBillMutation()

  const [voidBill, { isLoading: isLoadingVoidBill }] = useVoidBillMutation()

  const [createPayment, { isLoading: isCreatingPayment }] = useCreatePaymentMutation()

  const [updatePayment, { isLoading: isUpdatingPayment }] = useUpdatePaymentMutation()

  const [getInboundTicketsByIds, { data: inboundTicketsByIdsData, isLoading: isLoadingInboundTicketsByIds }] =
    useGetInboundTicketsByIdsMutation()
  const [getOutboundTicketsByIds, { data: outboundTicketsByIdsData, isLoading: isLoadingOutboundTicketsByIds }] =
    useGetOutboundTicketsByIdsMutation()

  const isLoadingTicketsByIds = useMemo(() => {
    if (isUpdate && !billData) return true
    if (isDirty || hasEditedInboundTickets) return isLoadingInboundTicketsByIds || isLoadingOutboundTicketsByIds
    if (billData?.inboundTicketIds?.length > 0 && !inboundTicketsByIdsData?.Items?.length) return true
    if (billData?.outboundTicketIds?.length > 0 && !outboundTicketsByIdsData?.Items?.length) return true
    return isLoadingInboundTicketsByIds || isLoadingOutboundTicketsByIds
  }, [
    isUpdate,
    isDirty,
    hasEditedInboundTickets,
    isLoadingInboundTicketsByIds,
    isLoadingOutboundTicketsByIds,
    billData,
    inboundTicketsByIdsData,
    outboundTicketsByIdsData,
  ])

  const isLoading =
    isLoadingTicketsByIds ||
    loadingPaymentsData ||
    isLoadingBill ||
    isLoadingCustomerData ||
    isLoadingCheckSignatureImage ||
    isLoadingUpdateBill ||
    isLoadingMergeBill ||
    isLoadingUpdateAndMergeBill ||
    isLoadingVoidBill ||
    isCreatingPayment ||
    isUpdatingPayment ||
    isFetchingPdf

  useGlobalLoader(isLoading)
  const showSkeletonLoaders = isUpdate && !billData

  const isInLockedAccountingPeriodV1 = useMemo(() => {
    if (enableLockAccountingV2) return false // if lock accounting V2 is turned on, we do not need to disable any fields
    if (!billData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return new Date(billData.completionDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()
  }, [lockAccountingPeriod, billData, enableLockAccountingV2])

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!enableLockAccountingV2) return false
    if (!billData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    if (hasLockedTickets) return true
    return checkIfDateIsLocked(lockAccountingPeriod, billData.completionDate)
  }, [lockAccountingPeriod, billData, enableLockAccountingV2, hasLockedTickets])

  const payingIsLocked = useMemo(() => {
    if (enableLockAccountingV2) return false // if lock accounting V2 is turned on, we do not need to disable any fields
    if (!lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return selectedTicketsData.some(ticket => {
      const inboundTicketIsLocked =
        new Date(ticket.effectiveDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()
      return (
        inboundTicketIsLocked &&
        ticket.status?.value !== TICKET_STATUSES.PRICE_COMPLETE.id &&
        ticket.status?.value !== TICKET_STATUSES.APPROVED.id
      )
    })
  }, [lockAccountingPeriod, selectedTicketsData, enableLockAccountingV2])

  const billCurrency = watch('details.currency')
  const paymentAuthorizationHook = usePaymentAuthorization(totalValue, billCurrency)

  const { pricePrepend } = useGetPricePrepend({ selectedCurrency: billCurrency })

  const materialTypeIds = useMemo(() => {
    const set = new Set()
    selectedTicketsData.forEach(ticket => {
      ticket?.materials?.forEach(material => {
        set.add(material.materialTypeId || '')
      })
    })
    return Array.from(set)
  }, [selectedTicketsData])

  const defaultPriceEntries = useMemo(
    () => ({
      Items: [
        {
          entriesObj: Object.values(cachedMaterialTypesData).map(materialType => ({
            ...(materialType?.defaultPriceEntry || {}),
            commodityId: materialType.commodityId,
            commodityName: materialType.commodityName,
            materialCode: materialType.code,
            materialName: materialType.commonName,
            tareWeightRequired: materialType.tareWeightRequired,
          })),
        },
      ],
    }),
    [cachedMaterialTypesData],
  )

  const validCustomerLocations = useMemo(() => {
    return customerData?.locations?.filter(location => locationIsNotEmpty(location)) || []
  }, [customerData])

  const fetchPaymentsData = async paymentIds => {
    await paymentIds
      .reduce(async (prevPromise, paymentId) => {
        const results = await prevPromise
        const response = await request(`/core-api/purchases/payments/${paymentId}`)
        return [...results, response]
      }, Promise.resolve([]))
      .then(results => {
        const filteredResults = results.filter(payment => payment.status !== 'VOID')

        const newBarcode = filteredResults.find(payment => payment.paymentMethod === 'atm' && payment.barcode)?.barcode
        setBarcode(newBarcode || null)

        setLoadingPaymentsData(false)
        setPaymentsData(filteredResults)
      })
      .catch(error => {
        setLoadingPaymentsData(false)
        const message = error?.data?.error || 'An error occurred loading the payments'
        dispatchSnackbar(dispatch, 'error', message)
      })
  }

  const getPaymentDetails = () => {
    const customerSelected = getValues('customer.name')
    let paymentMethod = ''

    if (customerSelected?.id) {
      paymentMethod = customerSelected?.preferredPaymentMethod
    }
    return { paymentMethod }
  }

  const dispatchFormSubmitEvent = (action, formattedPayments) => {
    if (formRef) {
      formRef.current.value = action
      formRef.current.formattedPayments = formattedPayments
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
    }
  }

  const handleClearMergeTicketsState = () => {
    if (mergeTicketsState?.shouldMergeTickets) {
      dispatch(clearMergeTicketsState())
    }
  }

  useEffect(() => {
    if (billType !== BILL_TYPES.FREIGHT.id) return
    if (!selectedCustomerId) {
      dispatch(
        setPaymentInfo({
          paymentMethod: defaultPaymentMethod,
          currency: defaultCurrency,
        }),
      )
      setValue('details.currency', defaultCurrency)
    } else if (customerData && customerData?.id === selectedCustomerId) {
      dispatch(
        setPaymentInfo({
          paymentMethod: customerData.preferredPaymentMethod || defaultPaymentMethod,
          currency: customerData.defaultCurrency || defaultCurrency,
        }),
      )
      setValue('details.currency', customerData?.defaultCurrency || defaultCurrency)
    }
  }, [customerData, selectedCustomerId, billType])

  useEffect(() => {
    if (billData) {
      const { status, customerId } = billData
      setSelectedCustomerId(customerId)
      setBillIsFullyPaid(status?.toUpperCase() === BILL_STATUSES.PAID.id)
      setPaymentMade(
        status?.toUpperCase() === BILL_STATUSES.PARTIAL.id || status?.toUpperCase() === BILL_STATUSES.PAID.id,
      )
      setValue('details.completionDate', billData?.completionDate || new Date())
      setValue('details.effectiveDate', billData?.effectiveDate || new Date())
      setValue('details.currency', billData?.currency || defaultCurrency)
      setValue('details.notes', billData?.notes || '')

      if (billData?.paymentIds?.length > 0) {
        setLoadingPaymentsData(true)
        fetchPaymentsData(billData.paymentIds)
      } else {
        setBarcode(null)
        setPaymentsData([])
      }
    }
  }, [billData])

  const pdfPrint = (pdfUrl, pdfTitle = '', documentType = PDF_TYPE.BILL_SETTLEMENT_DOC) => {
    setIsFetchingPdf(true)
    request(pdfUrl, 'get', {}, { accept: 'application/pdf' }, true)
      .then(async response => {
        // Get appropriate printer based on document type
        const printerKeyMap = {
          [PDF_TYPE.BILL_SETTLEMENT_DOC]: 'documentPrinter',
          [PDF_TYPE.CHECK]: 'checkPrinter',
        }
        const selectedPrinter = localStorage.getItem(printerKeyMap[documentType])

        if (!selectedPrinter || selectedPrinter === 'none') {
          // Use default printJS if no printer is selected
          printJS({
            printable: response,
            type: 'pdf',
            base64: true,
            documentTitle: pdfTitle || `Payment #${billData?.externalId}`,
          })
        } else {
          try {
            if (documentType === PDF_TYPE.CHECK) {
              await qzPrint(response, selectedPrinter, 1) // Pass explicit copies=1
            } else {
              await qzPrint(response, selectedPrinter)
            }
          } catch (error) {
            // Fallback to printJS if QZ fails
            printJS({
              printable: response,
              type: 'pdf',
              base64: true,
              documentTitle: pdfTitle || `Payment #${billData?.externalId}`,
            })
          }
        }
      })
      .catch(error => {
        dispatchSnackbar(dispatch, 'error', 'An error occurred while generating the PDF document')
      })
      .finally(() => {
        setIsFetchingPdf(false)
      })
  }

  useEffect(() => {
    tabsContext.setContainerTabsVisibility(false)

    const allCheckPaymentsNegative =
      paymentsData.length > 0 &&
      paymentsData
        .filter(payment => payment.paymentMethod === PAYMENT_METHODS_VALUES.CHECK.toLowerCase())
        .every(payment => payment.total < 0)

    if (billData) {
      contentContext.setContainerTitle(
        <Stack spacing={1} direction="row" alignItems="center">
          {isInLockedAccountingPeriodV2 && (
            <Tooltip
              title={generateLockTooltip(`${billType === BILL_TYPES.FREIGHT.id ? 'freight ' : ''}bill`)}
              placement="top"
            >
              <LockIcon color="info" />
            </Tooltip>
          )}
          <Typography variant="h5" data-testid="container-title">
            {`${billType === BILL_TYPES.FREIGHT.id ? 'Freight' : ''} Bill #${billData?.externalId}`}
          </Typography>
          {enableNegativeIbtToInvoice && billIsInvoiced ? (
            <InvoicedStatusChip
              showIcon={true}
              showTooltip={true}
              tooltipText={`This bill has been invoiced and cannot be edited.`}
            />
          ) : (
            <Chip
              icon={billIsFullyPaid ? <CheckCircleIcon /> : null}
              label={BILL_STATUSES[billData?.status?.toUpperCase()]?.name || startCase(toLower(billData?.status || ''))}
              sx={{
                '.MuiChip-icon': {
                  color: theme.palette.primary.main,
                },
              }}
            />
          )}
        </Stack>,
      )
    } else {
      contentContext.setContainerTitle(billType === BILL_TYPES.MATERIAL.id ? 'New Bill' : 'New Freight Bill')
    }
    const balanceTextAction = {
      text: (
        <Stack spacing={0.5} direction="row" alignItems="end">
          <Typography sx={{ fontSize: '14px', color: theme.palette.text.primary }}>{'Balance: '}</Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              color: theme.palette.text.primary,
            }}
          >
            {`${pricePrepend}${setDecimalPrecision(remainingBalance, { decimalPrecisionOverride: 2 })}`}
          </Typography>
        </Stack>
      ),
      id: 'balance',
      disabled: true,
      variant: 'text',
      onClick: () => {},
      sx: { textTransform: 'none' },
    }

    const makePaymentAction = {
      text: isUpdate ? 'MAKE A PAYMENT' : 'PAY THESE TICKETS',
      id: 'bill-submit-action-button',
      disabled:
        isLoading ||
        !selectedTicketsData?.length > 0 ||
        (isUpdate && (isDirty || hasEditedInboundTickets)) ||
        !paymentAuthorizationHook.isAuthorized ||
        payingIsLocked,
      onClick: () => setOpenBillPaymentsModal(true),
      type: ACTION_TYPE.TooltipButton,
      tooltipProps: {
        title: payingIsLocked ? ENTITY_IN_LOCK_PERIOD_MESSAGE : '',
      },
    }

    const INVOICE_NOT_FOUND_MESSAGE = 'Invoice not found for this bill'

    const invoiceAction = {
      text: '$ INVOICE',
      id: 'bill-invoice-action-button',
      disabled: isLoading || !billData?.invoiceId,
      onClick: () => {
        if (billData?.invoiceId) {
          navigate(`/app/sales/invoices/${billData?.invoiceId}`)
        } else {
          dispatchSnackbar(dispatch, 'error', INVOICE_NOT_FOUND_MESSAGE)
        }
      },
      type: ACTION_TYPE.TooltipButton,
      tooltipProps: {
        title: !billData?.invoiceId ? INVOICE_NOT_FOUND_MESSAGE : '',
      },
    }

    const emailAction = {
      text: 'EMAIL',
      icon: <EmailIcon />,
      disabled: isLoading,
      id: 'bill-email-action-button',
      variant: 'text',
      onClick: () => setIsEmailModalOpen(true),
    }

    const printAction = {
      text: 'PRINT',
      icon: <PrintIcon />,
      disabled: isLoading,
      id: 'bill-print-action-button',
      variant: 'text',
      onClick: () => {
        if (enableFePdfs && billTypeIsMaterial) {
          pdfPrint(`/core-api/purchases/bills/${billId}/pdf/${PDF_TYPE.BILL_SETTLEMENT_DOC}`)
        } else {
          setOpenPrintBillDialog(true)
        }
      },
    }

    const printCheckAction = {
      text: `PRINT CHECK${unprintedCheckPaymentsData.length > 1 ? 's' : ''}`,
      disabled: isLoading || unprintedCheckPaymentsData.length === 0 || allCheckPaymentsNegative,
      id: 'print-check-action-button',
      variant: 'contained',
      onClick: () => setOpenPrintCheckBlade(true),
      type: ACTION_TYPE.TooltipButton,
      tooltipProps: {
        title: allCheckPaymentsNegative
          ? 'Checks cannot be printed for negative amounts'
          : unprintedCheckPaymentsData.length === 0
            ? 'If you need to re-print a check, please void the payment first and then create a new check payment'
            : '',
      },
    }

    const voidAction = {
      text: 'VOID BILL',
      disabled: isLoading || isInLockedAccountingPeriodV1,
      icon: <UndoIcon />,
      id: 'bill-void-action-button',
      variant: 'text',
      color: 'error',
      onClick: () => dispatchFormSubmitEvent(BILL_ACTION.VOID),
      type: ACTION_TYPE.TooltipButton,
      tooltipProps: {
        title: isInLockedAccountingPeriodV1 ? ENTITY_IN_LOCK_PERIOD_MESSAGE : '',
      },
    }

    const dividerAction = {
      type: ACTION_TYPE.Divider,
    }

    const formActions = [
      {
        text: 'SAVE',
        id: 'bill-save-action-button',
        variant: isUpdate ? 'contained' : 'outlined',
        disabled: isLoading || !selectedTicketsData?.length > 0 || !selectedCustomerId,
        onClick: () => dispatchFormSubmitEvent(BILL_ACTION.SAVE),
      },
      {
        text: 'CANCEL',
        id: 'bill-cancel-action-button',
        variant: 'text',
        disabled: isLoading,
        onClick: () => {
          dispatch(clearPaymentInfo())
          handleClearMergeTicketsState()
          navigate(`/app/${billTypeIsMaterial ? 'purchases/bills' : 'freight/freight-bills'}/${billId || ''}`)
        },
      },
    ]

    if (!isUpdate) {
      contentContext.setContainerActions([balanceTextAction, ...formActions])
    } else if (billIsVoid) {
      contentContext.setContainerActions([balanceTextAction])
    } else if (billIsInvoiced) {
      contentContext.setContainerActions([
        balanceTextAction,
        invoiceAction,
        ...(enablePdfEmails && billTypeIsMaterial ? [emailAction, printAction] : [printAction]),
      ])
    } else if (billIsFullyPaid) {
      if (checkPaymentsData.length > 0) {
        contentContext.setContainerActions([
          balanceTextAction,
          printCheckAction,
          ...(enablePdfEmails && billTypeIsMaterial ? [emailAction, printAction] : [printAction]),
        ])
      } else {
        contentContext.setContainerActions([
          balanceTextAction,
          ...(enablePdfEmails && billTypeIsMaterial ? [emailAction, printAction] : [printAction]),
        ])
      }
    } else if (paymentMade) {
      contentContext.setContainerActions([
        balanceTextAction,
        makePaymentAction,
        ...(enablePdfEmails && billTypeIsMaterial ? [emailAction, printAction] : [printAction]),
      ])
    } else if (isDirty || hasEditedInboundTickets) {
      contentContext.setContainerActions([balanceTextAction, ...formActions])
    } else if (billType === BILL_TYPES.MATERIAL.id) {
      contentContext.setContainerActions([
        balanceTextAction,
        makePaymentAction,
        ...(enablePdfEmails && billTypeIsMaterial ? [emailAction, printAction, dividerAction] : [printAction]),
        voidAction,
      ])
    } else {
      contentContext.setContainerActions([
        balanceTextAction,
        makePaymentAction,
        ...(enablePdfEmails && billTypeIsMaterial ? [emailAction, printAction] : [printAction]),
      ])
    }
  }, [
    billData,
    billIsVoid,
    billIsInvoiced,
    billType,
    remainingBalance,
    paymentMade,
    billIsFullyPaid,
    selectedTicketsData,
    hasEditedInboundTickets,
    isDirty,
    paymentsData,
    isLoading,
    pricePrepend,
    paymentAuthorizationHook.isAuthorized,
    isInLockedAccountingPeriodV1,
    isInLockedAccountingPeriodV2,
    payingIsLocked,
    selectedCustomerId,
    billTypeIsMaterial,
  ])

  useEffect(() => {
    if (customerData?.id === selectedCustomerId) {
      setValue('customer.name', customerData)
    }
  }, [customerData, selectedCustomerId])

  useEffect(() => {
    const aggregatedData = selectedTicketsData.reduce(
      (aggregate, ticket) => {
        const ticketSubtotal = ticket?.subTotal || 0
        return {
          gross: roundTo(aggregate.gross + (ticket?.gross || 0), ROUNDING_PRECISIONS.WEIGHT),
          tare: roundTo(aggregate.tare + (ticket?.tare || 0), ROUNDING_PRECISIONS.WEIGHT),
          adjustments: roundTo(aggregate.adjustments + (ticket?.adjustments || 0), ROUNDING_PRECISIONS.WEIGHT),
          net: roundTo(aggregate.net + (ticket?.netWeight || 0), ROUNDING_PRECISIONS.WEIGHT),
          subtotal: roundTo(aggregate.subtotal + ticketSubtotal, ROUNDING_PRECISIONS.COST),
          freightCost: roundTo(aggregate.freightCost + (ticket?.freightCost?.cost || 0), ROUNDING_PRECISIONS.COST),
        }
      },
      { gross: 0, tare: 0, adjustments: 0, net: 0, subtotal: 0, freightCost: 0 },
    )

    const aggregatedSubtotal = roundTo(
      (billType === BILL_TYPES.MATERIAL.id ? aggregatedData.subtotal : aggregatedData.freightCost) +
        Number(billData?.appliedRoundingDelta || 0),
      ROUNDING_PRECISIONS.COST,
    )
    const aggregatedTax = roundTo((aggregatedSubtotal * billTaxRate) / 100, ROUNDING_PRECISIONS.COST)
    const aggregatedTotal = roundTo(aggregatedSubtotal + aggregatedTax, ROUNDING_PRECISIONS.COST)

    setTotalGross(aggregatedData.gross)
    setTotalTare(aggregatedData.tare)
    setTotalAdjustments(aggregatedData.adjustments)
    setTotalNet(aggregatedData.net)
    setSubtotalValue(aggregatedSubtotal)
    setTaxValue(aggregatedTax)
    setTotalValue(aggregatedTotal)
    // note: billData?.total is the amount paid so far
    setRemainingBalance(roundTo(aggregatedTotal - (billData?.total || 0), ROUNDING_PRECISIONS.COST))
  }, [selectedTicketsData, billTaxRate, billType, paymentsData])

  const getMaterialsData = () => selectedTicketsData?.map(ticket => ticket?.materials).flat()

  const onSubmit = async (data, e) => {
    if (materialsHasTareRequiredAndMissing(getMaterialsData())) {
      setErrorMessage('Tare weight is required for some materials.')
      return
    }
    const submitAction = e.target.value
    if (submitAction === BILL_ACTION.SAVE || submitAction === BILL_ACTION.PAY) {
      try {
        let newMergedBillData
        if (hasEditedInboundTickets) {
          const selectedInboundTicketIds = selectedTicketsData
            ?.filter(ticket => ticket.ticketType === 'IBT')
            .map(ticket => ticket?.id)
          const selectedOutboundTicketIds = selectedTicketsData
            ?.filter(ticket => ticket.ticketType === 'OBT')
            .map(ticket => ticket?.id)
          // if bill already exists, then unmerge all previously linked bills and then remerge selected bills
          if (isUpdate) {
            const { data: updateAndMergeBillResponse, error: updateAndMergeBillError } = await updateAndMergeBill({
              billId,
              inboundTicketIds: selectedInboundTicketIds,
              outboundTicketIds: selectedOutboundTicketIds,
              body: {
                inboundTicketIds: selectedInboundTicketIds,
                outboundTicketIds: selectedOutboundTicketIds,
                billType,
              },
            })

            if (updateAndMergeBillError) throw new Error(updateAndMergeBillError?.data?.error)
            const { mergedBill } = updateAndMergeBillResponse
            if (!mergedBill) throw new Error('New Bill Data is not available in the response')
            newMergedBillData = mergedBill
          } else {
            // choose highest ticket's bill id, merge only
            const updatedSelectedTickets = selectedTicketsData.map(ticket => ({
              id: ticket.id,
              externalId: isNaN(Number(ticket.ticketId)) ? ticket.ticketId : Number(ticket.ticketId),
              billId: billType === BILL_TYPES.MATERIAL.id ? ticket.billId : ticket.freightCost?.freightBillId,
            }))
            const highestTicket = updatedSelectedTickets.sort((a, b) => {
              if (a.externalId < b.externalId) return 1
              if (a.externalId > b.externalId) return -1
              return 0
            })?.[0]
            const { data: mergeBillData, error: mergeBillError } = await mergeBill({
              billId: highestTicket?.billId,
              inboundTicketIds: selectedInboundTicketIds,
              outboundTicketIds: selectedOutboundTicketIds,
              body: {
                billIds: updatedSelectedTickets?.map(ticket => ticket?.billId),
              },
            })
            if (mergeBillError) throw new Error(mergeBillError?.data?.error)
            newMergedBillData = mergeBillData
          }
        }

        // patch any bill changes
        const { data: updateBillData, error: updateBillError } = await updateBill({
          billId: newMergedBillData?.id || billId,
          body: {
            id: newMergedBillData?.id || billId,
            completionDate: (data.details?.completionDate
              ? new Date(data.details.completionDate)
              : new Date()
            ).toISOString(),
            effectiveDate: data.details.effectiveDate ? data.details.effectiveDate : undefined,
            foreignCurrency: data.details?.currency,
            notes: data.details?.notes,
            customerId: billType === BILL_TYPES.FREIGHT.id ? selectedCustomerId : undefined,
            customerDisplayName: billType === BILL_TYPES.FREIGHT.id ? customerData?.displayName : undefined,
          },
        })
        if (updateBillError) throw new Error(updateBillError?.data?.error)

        if (submitAction === BILL_ACTION.PAY) {
          const { formattedPayments } = e.target
          const multiplePayments = formattedPayments.length > 1

          const createPaymentResponses = await formattedPayments.reduce(async (prevPromise, formattedPayment) => {
            const results = await prevPromise
            const response = await createPayment({
              billId: newMergedBillData?.id || billId,
              batchBillId: newMergedBillData?.batchBillId,
              paymentCreation: formattedPayment,
            })
            return [...results, response]
          }, Promise.resolve([]))
          const createPaymentError = createPaymentResponses.find(response => !!response.error)?.error
          if (createPaymentError) throw new Error(createPaymentError?.data?.error)
          dispatchSnackbar(dispatch, 'success', `Payment${multiplePayments ? 's' : ''} confirmed`)
        } else {
          dispatchSnackbar(dispatch, 'success', 'Bill Saved')
        }
        dispatch(clearTransportationInformation())
        handleClearMergeTicketsState()
        navigate(
          `/app/${billType === BILL_TYPES.MATERIAL.id ? 'purchases/bills' : 'freight/freight-bills'}/${updateBillData.id}`,
        )
      } catch (error) {
        const action = submitAction === BILL_ACTION.SAVE ? 'saving' : 'paying'
        const message = error?.data?.error || error?.message || `An error occurred while ${action} the bill`
        dispatchSnackbar(dispatch, 'error', message)
      }
    } else if (submitAction === BILL_ACTION.VOID) {
      await voidBill({
        billId,
        batchBillId: billData?.batchBillId,
        inboundTicketIds: selectedTicketsData?.filter(ticket => ticket.ticketType === 'IBT').map(ticket => ticket?.id),
        outboundTicketIds: selectedTicketsData?.filter(ticket => ticket.ticketType === 'OBT').map(ticket => ticket?.id),
        body: {
          inboundTicketIds: [],
          outboundTicketIds: [],
        },
      })
        .unwrap()
        .then(() => {
          dispatchSnackbar(dispatch, 'success', 'Bill Voided')
          handleClearMergeTicketsState()
          navigate(`/app/${billType === BILL_TYPES.MATERIAL.id ? 'purchases/bills' : 'freight/freight-bills'}`)
        })
        .catch(error => {
          const errorMsg = error?.data?.error || error?.error || error?.message || 'An error occurred voiding the bill.'
          setErrorMessage(errorMsg)
        })
    }
  }

  const onPrintCheck = async () => {
    setLoadingPaymentsData(true)
    await unprintedCheckPaymentsData
      .reduce(async (prevPromise, payment) => {
        const results = await prevPromise
        const checkingAccountId = checkPrintGetValues(`${payment.id}.details.payFrom`)
        const sanitizedCheckingAccountId = checkingAccountId === 'Main Checking Account' ? undefined : checkingAccountId

        const formattedData = {
          id: payment.id,
          billId: payment.billId,
          paymentDate: new Date(payment.paymentDate).getTime(),
          completionDate: new Date(payment.completionDate).getTime(),
          paymentMethod: payment.paymentMethod,
          accountId: sanitizedCheckingAccountId,
          payToContactId: checkPrintGetValues(`${payment.id}.information.payToTheOrder`)?.id,
          payToLocationId: checkPrintGetValues(`${payment.id}.information.location`)?.id,
          paymentNumber: payment.paymentNumber?.toString() || '',
          status: payment.status,
          total: {
            amount: payment.total * 100,
            currency: billData?.currency || defaultCurrency,
            precision: 2,
          },
          inboundTickets: payment.inboundTickets?.map(inboundTicket => inboundTicket.id) || [],
          checkNumber: checkPrintGetValues(`${payment.id}.details.checkNumber`),
          isManualCheckNumber: !checkPrintGetValues(`${payment.id}.details.useAutoincrementedCheckNumber`),
          checkMemo: checkPrintGetValues(`${payment.id}.information.memo`),
        }
        const response = await updatePayment({ paymentId: payment.id, payment: formattedData })
        return [...results, response]
      }, Promise.resolve([]))
      .then(response => {
        const responsePaymentDict = {}
        response.forEach(payment => {
          const paymentData = payment?.data || {}
          responsePaymentDict[paymentData.id] = paymentData
        })
        setPaymentsData(paymentsData.map(payment => responsePaymentDict[payment.id] || payment))
      })
      .catch(error => {
        const message =
          error?.data?.error ||
          `An error occurred updating the payment${unprintedCheckPaymentsData.length > 0 ? 's' : ''}`
        dispatchSnackbar(dispatch, 'error', message)
      })
    setLoadingPaymentsData(false)
  }

  useEffect(() => {
    return () => handleClearMergeTicketsState()
  }, [])

  const selectedTicketsDataArray = useMemo(() => {
    return selectedTicketsData ? [selectedTicketsData] : []
  }, [selectedTicketsData])

  const customersDataArray = useMemo(() => {
    return customerData ? [customerData] : []
  }, [customerData])

  return (
    <>
      <Stack minHeight="calc(100vh - 235px)">
        <AlertSnackbar
          message={errorMessage}
          isOpen={!!errorMessage}
          severity="error"
          handleClose={() => setErrorMessage('')}
        />
        {openBillPaymentsModal && (
          <BillPaymentsModal
            title="Payment Methods"
            buttonTitle="Confirm Payment"
            open={openBillPaymentsModal}
            onClose={() => setOpenBillPaymentsModal(false)}
            billData={billData}
            billTaxRate={billTaxRate}
            paymentsData={paymentsData}
            customerData={customerData}
            selectedTicketsData={selectedTicketsData}
            isLoading={isLoading}
            isMergingAndCreatingPayment={isCreatingPayment}
            getBillValues={getValues}
            dispatchFormSubmitEvent={dispatchFormSubmitEvent}
            pricePrepend={pricePrepend}
          />
        )}
        <PrintCheckBlade
          open={openPrintCheckBlade}
          setOpen={setOpenPrintCheckBlade}
          checkPaymentsData={unprintedCheckPaymentsData}
          selectedTicketsData={selectedTicketsDataArray}
          customersData={customersDataArray}
          handleSubmit={checkPrintHandleSubmit}
          setValue={checkPrintSetValue}
          watch={checkPrintWatch}
          trigger={checkPrintTrigger}
          control={checkPrintControl}
          reset={checkPrintReset}
          errors={checkPrintErrors}
          isValid={checkPrintIsValid}
          onPrint={async () => {
            if (enableFePdfs) {
              await onPrintCheck()
              setOpenPrintCheckBlade(false)
              pdfPrint(`/core-api/purchases/payments/${billId}/pdf/${PDF_TYPE.CHECK}`, '', PDF_TYPE.CHECK)
            } else {
              setOpenPrintCheckBlade(false)
              setOpenPrintCheckPreview(true)
            }
          }}
          pricePrepend={pricePrepend}
        />
        {openPrintCheckPreview && (
          <PrintDialog
            title="Print Check Info to Template"
            open={openPrintCheckPreview}
            onClose={() => setOpenPrintCheckPreview(false)}
            dialogId="print-check-dialog"
            shouldPrintTitle={false}
            dialogType={PRINT_DIALOG_TYPES.CHECK}
            onAfterPrint={async () => {
              await onPrintCheck()
            }}
          >
            {unprintedCheckPaymentsData.map((payment, index) => (
              <Box key={payment.id} className={index > 0 ? 'pagebreak' : null} pt={index > 0 ? 3.3 : 0}>
                <CheckTemplate
                  key={payment.id}
                  checkRecipient={
                    [
                      checkPrintGetValues(`${payment.id}.information.payToTheOrder`).firstName,
                      checkPrintGetValues(`${payment.id}.information.payToTheOrder`).lastName,
                    ]
                      .join(' ')
                      .trim() ||
                    selectedTicketsData?.[0]?.customer?.displayName ||
                    selectedTicketsData?.[0]?.customerName
                  }
                  customerAddress={
                    checkPrintGetValues(`${payment.id}.information.location`) ||
                    validCustomerLocations[0] ||
                    getContactAddress(customerData)
                  }
                  textAmount={CurrencyToWords(parseFloat(checkPrintGetValues(`${payment.id}.details.checkAmount`)))}
                  paymentDate={dateFormatter(payment.paymentDate)}
                  amount={parseFloat(checkPrintGetValues(`${payment.id}.details.checkAmount`)).toFixed(2)}
                  memo={checkPrintGetValues(`${payment.id}.information.memo`)}
                  checkSignatureImageSrc={checkSignatureImageSrc}
                />
              </Box>
            ))}
          </PrintDialog>
        )}
        {openPrintBillDialog && (
          <PrintDialog
            title={`Payment #${billData?.externalId}`}
            open={openPrintBillDialog}
            onClose={() => setOpenPrintBillDialog(false)}
            showLogoAddress
            headerOverride={
              <>
                {barcode && (
                  <Box sx={{ display: 'flex', justifyContent: 'right', paddingRight: 2 }}>
                    <Barcode width={3} value={barcode} />
                  </Box>
                )}
              </>
            }
          >
            <PrintableBill
              customerData={customerData}
              paymentsData={paymentsData}
              billData={billData}
              billTaxRate={billTaxRate}
              notes={billData?.notes}
              tickets={selectedTicketsData}
              priceSheet={null}
              priceEntriesByPurchaseOrderId={billData?.priceEntriesByPurchaseOrderId}
              defaultPriceSheet={defaultPriceEntries}
              summaryData={{
                totalGross,
                totalTare,
                totalAdjustments,
                totalNet,
                totalValue,
                remainingBalance,
              }}
              pricePrepend={pricePrepend}
            />
          </PrintDialog>
        )}
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <Stack>
            {!paymentAuthorizationHook.isAuthorized && (
              <HighBalancePaymentAlert
                thresholdAmount={paymentAuthorizationHook.ticketValueThreshold}
                thresholdAmountFc={paymentAuthorizationHook.convertedThreshold}
                paymentCurrency={billCurrency}
              />
            )}
            <Stack p={3} direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <Box width={{ xs: '100%', md: '50%' }}>
                <Typography variant="h6" gutterBottom color="textSecondary">
                  {billType === BILL_TYPES.MATERIAL.id ? 'Customer Information' : 'Carrier Information'}
                </Typography>
                <Stack
                  direction="row"
                  padding={2}
                  spacing={2}
                  sx={{
                    backgroundColor: 'background.section',
                    borderRadius: '8px',
                  }}
                >
                  <SkeletonWrapper isLoading={showSkeletonLoaders}>
                    <CustomerAutocomplete
                      name="customer.name"
                      label={billType === BILL_TYPES.MATERIAL.id ? 'Customer Name' : 'Carrier Name'}
                      data-test="customer-name"
                      variant="outlined"
                      size="small"
                      control={control}
                      freeSolo={false}
                      disabled={
                        billIsVoid || billIsInvoiced || paymentMade || (isUpdate && billType !== BILL_TYPES.FREIGHT.id)
                      }
                      error={!!errors.customer?.name}
                      helperText={errors.customer?.name?.message}
                      baseFilter={
                        billType === BILL_TYPES.FREIGHT.id ? CUSTOMERS_FILTERS.CARRIER : CUSTOMERS_FILTERS.SUPPLIER
                      }
                      onChange={customer => {
                        if (customer) {
                          setSelectedCustomerId(customer.id)
                        } else {
                          setSelectedCustomerId(null)
                          if (billType !== BILL_TYPES.FREIGHT.id) {
                            setSelectedTicketsData([])
                          }
                          dispatch(clearPaymentInfo())
                        }
                      }}
                      autocompleteProps={{
                        renderOption: (props, option) => (
                          <li {...props} key={option.id}>
                            {option.displayName}
                          </li>
                        ),
                      }}
                    />
                  </SkeletonWrapper>
                </Stack>
              </Box>
              <Box width={{ xs: '100%', md: '50%' }}>
                <Typography variant="h6" gutterBottom color="textSecondary">
                  Details
                </Typography>
                <Stack
                  direction="column"
                  padding={2}
                  spacing={2}
                  sx={{
                    backgroundColor: 'background.section',
                    borderRadius: '8px',
                  }}
                >
                  <Stack direction="row" spacing={2}>
                    <SkeletonWrapper isLoading={showSkeletonLoaders}>
                      <DatePicker
                        name="details.effectiveDate"
                        label="Effective Date"
                        data-test="details-effectiveDate"
                        variant="outlined"
                        size="small"
                        error={!!errors.effectiveDate}
                        helperText={errors.effectiveDate?.message}
                        control={control}
                        disabled={billIsVoid || billIsInvoiced || paymentMade}
                        datePickerProps={{
                          disabled: billIsVoid || billIsInvoiced || paymentMade,
                          minDate: lockAccountingPeriod.isLockedForAccounting
                            ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                            : undefined,
                        }}
                        defaultDate={new Date()}
                        InputLabelProps={{ shrink: true }}
                      />
                    </SkeletonWrapper>
                    <SkeletonWrapper isLoading={showSkeletonLoaders}>
                      <DatePicker
                        name="details.completionDate"
                        label="Completion Date"
                        data-test="details-completionDate"
                        variant="outlined"
                        size="small"
                        error={!!errors.completionDate}
                        helperText={errors.completionDate?.message}
                        control={control}
                        disabled={billIsVoid || billIsInvoiced || paymentMade}
                        datePickerProps={{
                          disabled: billIsVoid || billIsInvoiced || paymentMade,
                          minDate: lockAccountingPeriod.isLockedForAccounting
                            ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                            : undefined,
                        }}
                        defaultDate={new Date()}
                        InputLabelProps={{ shrink: true }}
                      />
                    </SkeletonWrapper>
                    <SkeletonWrapper isLoading={showSkeletonLoaders}>
                      <Select
                        name="details.currency"
                        label="Currency"
                        data-test="details-currency"
                        size="small"
                        control={control}
                        defaultValue={defaultCurrency}
                        items={CURRENCY_OPTIONS}
                        disabled
                        InputLabelProps={{ shrink: true }}
                      />
                    </SkeletonWrapper>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
            {billData?.paymentIds?.length > 0 && (
              <BillPaymentsTable
                billId={billId}
                paymentIds={billData.paymentIds}
                paymentsData={paymentsData}
                loadingPaymentsData={loadingPaymentsData}
                setLoadingPaymentsData={setLoadingPaymentsData}
                fetchPaymentsData={fetchPaymentsData}
                pricePrepend={pricePrepend}
                disabled={billIsVoid || billIsInvoiced}
              />
            )}
            {materialTypeIds.map(materialTypeId => (
              <MaterialTypesDataFetcher
                key={materialTypeId}
                materialTypeId={materialTypeId}
                setCachedMaterialTypesData={setCachedMaterialTypesData}
              />
            ))}
            <BillTicketsTable
              billData={billData}
              billType={billType}
              customerData={customerData}
              selectedTicketsData={selectedTicketsData}
              setSelectedTicketsData={setSelectedTicketsData}
              setSelectedCustomerId={setSelectedCustomerId}
              getInboundTicketsByIds={getInboundTicketsByIds}
              getOutboundTicketsByIds={getOutboundTicketsByIds}
              isLoadingTicketsByIds={isLoadingTicketsByIds}
              setHasEditedInboundTickets={setHasEditedInboundTickets}
              setErrorMessage={setErrorMessage}
              paymentMade={paymentMade}
              billIsFullyPaid={billIsFullyPaid}
              billIsInvoiced={billIsInvoiced}
              getValues={getValues}
              totalNet={totalNet}
              subtotalValue={subtotalValue}
              taxValue={taxValue}
              totalValue={totalValue}
              pricePrepend={pricePrepend}
              setHasLockedTickets={setHasLockedTickets}
            />
          </Stack>
        </form>
      </Stack>
      <EmailModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        recipients={customerData?.contacts?.map(contact => contact.email).filter(Boolean)}
        pdfTypeOptions={[PDF_TYPE.BILL_SETTLEMENT_DOC]}
        defaultPdfType={PDF_TYPE.BILL_SETTLEMENT_DOC}
        entityId={billId}
      />
      <BillFooter
        disabled={billIsVoid || billIsInvoiced || billIsFullyPaid}
        control={control}
        errors={errors}
        totalGross={totalGross}
        totalTare={totalTare}
        totalNet={totalNet}
        subtotalValue={subtotalValue}
        taxValue={taxValue}
        totalValue={totalValue}
        pricePrepend={pricePrepend}
      />
    </>
  )
}

BillContainer.propTypes = {
  defaultBillType: PropTypes.oneOf([BILL_TYPES.MATERIAL.id, BILL_TYPES.FREIGHT.id]).isRequired,
}

export default BillContainer
